import _ from 'underscore';
import { stringifySync } from '@bingads-webui/safe-json';
import { IQuestionConfigurator, SurveyConfiguration, Others } from '../../survey.types';
import { PageConfigurator } from './page';

export class SurveyConfigurator {
  featureArea: string;
  pages: PageConfigurator[];
  serverContext: any;
  i18n: any;
  endingPage: PageConfigurator;
  questions: Map<string, IQuestionConfigurator> = new Map();
  configuration: SurveyConfiguration;

  constructor({
    featureArea, serverContext, i18n, configuration,
  }) {
    this.featureArea = featureArea;
    this.pages = [];
    this.serverContext = serverContext;
    this.i18n = i18n;
    this.endingPage = new PageConfigurator({
      serverContext: this.serverContext, i18n: this.i18n, surveyConfigurator: this,
    });
    this.configuration = configuration;
  }

  // feature area is used to classify the feedback. You can find and create feature areas at
  // https://ocv.microsoft.com/#/manage/?aid=a9bfe3b9-40d8-443d-9d80-ba3369aa1463
  getFeatureArea() {
    return this.featureArea;
  }

  // sort the questions by page and question position
  sortPages(): void {
    this.pages.forEach(page => page.sortQuestions());
  }

  // get the page at index, or create it if it doesn't exist
  getPageIfNoneCreateIt(pageIndex: number): PageConfigurator {
    let page = this.pages[pageIndex];
    if (page == null) {
      this.pages[pageIndex] = new PageConfigurator({
        serverContext: this.serverContext, i18n: this.i18n, surveyConfigurator: this,
      });
      page = this.pages[pageIndex];
    }
    return page;
  }

  hasPageAtIndex(pageIndex: number, answers: any): boolean {
    const page = this.pages[pageIndex];
    if (page == null || !page.isVisible(answers)) {
      return false;
    }
    return true;
  }

  // get the page at index
  getPage(pageIndex: number): PageConfigurator {
    return this.pages[pageIndex];
  }

  // get the ending page, the page after submitting the survey
  getEndingPage(): PageConfigurator {
    return this.endingPage;
  }

  // add a question to the survey
  addQuestion(questionConfiguration): void {
    const { page: pageIndex, key } = questionConfiguration;
    const page = this.getPageIfNoneCreateIt(pageIndex);
    const question = page.addQuestion(questionConfiguration);
    this.questions.set(key, question);
  }

  // get question by key for question with question integration
  getQuestionByKey(key: string) {
    return this.questions.get(key);
  }

  // add questions to the survey
  addQuestions(questionConfigurations): void {
    questionConfigurations.forEach(questionConfiguration => this.addQuestion(questionConfiguration));
  }

  // get all the default answers for the survey
  getDefaultAnswers(): Object {
    const answers = {};
    this.pages.forEach((page) => {
      page.questions.forEach((question) => {
        answers[question.key] = question.getDefaultData();
      });
    });
    return answers;
  }

  // get all the default dirty state for the survey
  getDefaultDirtyState(bool: boolean): Object {
    const state = {};
    this.pages.forEach((page) => {
      page.questions.forEach((question) => {
        state[question.key] = question.getDefaultDirtyState(bool);
      });
    });
    return state;
  }

  getDefaultDirtyStatePerPage(bool: boolean, pageIndex: number): Object {
    const state = {};
    this.pages[pageIndex].questions.forEach((question) => {
      state[question.key] = question.getDefaultDirtyState(bool);
    });
    return state;
  }

  setConfiguration(configuration: SurveyConfiguration) {
    this.configuration = configuration;
  }

  getConfiguration(): SurveyConfiguration {
    return this.configuration;
  }

  getOtherFlags(): Others {
    const {
      others = {},
    } = this.getConfiguration();

    return others;
  }

  // get the feedback data for sending to the OCV service
  getFeedbackData(answers: Object): { feedback, extendedManifestData } {
    let remaining: Array<Object> = [];
    let comments: Array<Object> = [];
    let email = null;
    let ratingObject: any = null;

    this.pages.forEach((page) => {
      if (page.isVisible(answers)) {
        const {
          email: emailData,
          rating: ratingData,
          remaining: remainingData,
          comments: commentsData,
        } = page.getFeedbackData(answers);

        if (emailData != null) {
          email = emailData;
        }
        if (ratingData != null) {
          ratingObject = ratingData;
        }

        comments = comments.concat(commentsData);
        remaining = remaining.concat(remainingData);
      }
    });

    let comment;

    if (comments.length > 0) {
      // only one comment could show in the OCV,
      // the remaining comments will be put inside remaining
      comment = comments.shift();
      remaining = remaining.concat(comments);
    }

    const { combineTextInputToComment } = this.getOtherFlags();

    if (combineTextInputToComment) {
      if (_.isEmpty(comment)) {
        comment = {
          answer: '',
          type: 'comment',
        };
      } else if (_.isEmpty(comment.answer)) {
        comment.answer = '';
      }
      remaining.forEach((question: any) => {
        if (question.type === 'single-selection') {
          if (!_.isEmpty(question.answer.input)) {
            comment.answer = comment.answer.concat(` ${question.answer.input}`);
          }
        } else if (question.type === 'multiple-selection') {
          question.answer.forEach((optionAnswer) => {
            if (!_.isEmpty(optionAnswer.input)) {
              comment.answer = comment.answer.concat(` ${optionAnswer.input}`);
            }
          });
        }
      });
    }

    const {
      rating = null,
      ratingOptionsCount,
    } = ratingObject || {};

    const feedback = {
      rating: rating == null ? -1 : rating,
      email,
      comment: comment?.answer,
    };

    const questions = {};
    for (let q = 0; q < remaining.length; q += 1) {
      questions[`question${q}`] = stringifySync(remaining[q]);
    }
    questions['questionCount'] = remaining.length;

    const extendedManifestData = {
      surveySpecificData: {
        comment: feedback.comment ? [feedback.comment] : [],
        rating: feedback.rating !== -1 ? [{ selectedRating: `${Math.round(feedback.rating * ratingOptionsCount) - 1}` }] : [],
        questions,
        hasEmail: !_.isNull(email),
      },
    };

    return {
      feedback,
      extendedManifestData,
    };
  }
}
